import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Media, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faCalendarAlt,
  faCertificate,
  faClipboardCheck,
  faImage,
  faMapMarkerAlt,
  faStar,
  faTruck
} from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Avatar from '../../common/Avatar'
import LightBoxGallery from '../../common/LightBoxGallery'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import { getMarketplaceService } from '../../../helpers/api/marketplace'
import {
  categoryIcon,
  formatAvailability,
  formatPriceRange,
  formatVisitFee,
  pricingModeShort,
  providerCardData,
  providerLocation,
  storageUrl
} from '../marketplaceConstants'
import ServiceRequestModal from './ServiceRequestModal'

const REQUEST_STATUS = {
  pending: 'pendiente de respuesta del proveedor',
  visited: 'con la visita hecha',
  quoted: 'con una cotización por revisar'
}

const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '')

const Gallery = ({ photos }) => {
  const [current, setCurrent] = useState(0)
  const images = photos.map((photo) => storageUrl(photo.path))

  if (!images.length) {
    return (
      <div className="bg-200 d-flex align-items-center justify-content-center text-400" style={{ height: 320 }}>
        <FontAwesomeIcon icon={faImage} size="3x" />
      </div>
    )
  }

  return (
    <LightBoxGallery images={images}>
      {(openImage) => (
        <>
          <button type="button" className="d-block w-100 p-0 border-0 bg-200" onClick={() => openImage(current)} title="Ver en grande">
            <img src={images[current]} alt="" className="w-100" style={{ height: 360, objectFit: 'cover' }} />
          </button>
          {images.length > 1 && (
            <div className="d-flex flex-wrap p-2" style={{ gap: 8 }}>
              {images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  onClick={() => setCurrent(index)}
                  className={`p-0 rounded overflow-hidden border ${index === current ? 'border-primary' : 'border-200'}`}
                  style={{ width: 72, height: 72, borderWidth: 2 }}
                >
                  <img src={image} alt="" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </LightBoxGallery>
  )
}

const ReviewsSection = () => (
  <Card className="mb-3">
    <CardHeader className="bg-light">
      <h6 className="mb-0">Calificaciones y reseñas</h6>
    </CardHeader>
    <CardBody className="text-center py-4">
      <div className="mb-2 text-300">
        {[1, 2, 3, 4, 5].map((star) => (
          <FontAwesomeIcon key={star} icon={faStar} className="mx-1" />
        ))}
      </div>
      <p className="font-weight-semi-bold mb-1">Aún no tiene reseñas</p>
      <p className="fs--1 text-600 mb-0">Cuando los propietarios que contraten este servicio lo califiquen, verás aquí sus opiniones.</p>
    </CardBody>
  </Card>
)

const ProviderCard = ({ provider }) => {
  const card = providerCardData(provider)
  const location = providerLocation(provider)

  return (
    <Card className="mb-3">
      <CardHeader className="bg-light">
        <h6 className="mb-0">Proveedor</h6>
      </CardHeader>
      <CardBody>
        <Media className="align-items-center mb-3">
          <Avatar src={card.photoUrl} name={card.name} size="3xl" rounded={card.isCompany ? 'soft' : 'circle'} className="mr-3" />
          <Media body>
            <h5 className="fs-0 mb-1">{card.name}</h5>
            <p className="fs--1 text-600 mb-0">{card.isCompany ? 'Empresa' : 'Persona natural'}</p>
            {card.verified && (
              <Badge color="soft-primary" pill className="mt-1">
                <FontAwesomeIcon icon={faCertificate} className="mr-1" />
                Sello Homelan
              </Badge>
            )}
          </Media>
        </Media>
        {location && (
          <p className="fs--1 text-700 mb-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-500 mr-2" />
            {location}
          </p>
        )}
        {provider.coverage_zones?.length > 0 && (
          <>
            <p className="fs--1 text-600 mb-1">Zonas que atiende</p>
            <div className="d-flex flex-wrap mb-3" style={{ gap: 4 }}>
              {provider.coverage_zones.map((zone) => (
                <Badge key={zone} color="soft-secondary" pill>
                  {zone}
                </Badge>
              ))}
            </div>
          </>
        )}
        <Link to={`/dashboard/marketplace?provider=${provider.id}`} className="fs--1 font-weight-semi-bold">
          Ver todos sus servicios
        </Link>
      </CardBody>
    </Card>
  )
}

const OpenRequestAlert = ({ request }) => (
  <Alert color="info" className="fs--1 mb-0">
    <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
    Ya enviaste una solicitud para este servicio el {formatDate(request.created_at)}, para el{' '}
    {formatDate(request.suggested_date)} a las {request.suggested_time}. Está {REQUEST_STATUS[request.status] || 'abierta'}.
    <Link to={`/dashboard/marketplace/requests/${request.id}`} className="d-block font-weight-semi-bold mt-2">
      Ver conversación
    </Link>
  </Alert>
)

const ServiceSummaryCard = ({ service, onRequest, className }) => (
  <Card className={`mb-3 ${className}`}>
    <CardBody>
      {service.category && (
        <Badge color="soft-primary" pill className="mb-2">
          <FontAwesomeIcon icon={categoryIcon(service.category)} className="mr-1" />
          {service.category.name}
        </Badge>
      )}
      <h4 className="mb-3">{service.title}</h4>
      <p className="fs--1 text-600 mb-1">Rango de precios estimado</p>
      <p className="mb-3">
        <span className="fs-2 font-weight-bold text-900">{formatPriceRange(service.price_min, service.price_max)}</span>
        <span className="fs--1 text-600 ml-1">{pricingModeShort(service.pricing_mode)}</span>
      </p>
      <p className="fs--1 text-700 mb-2">
        <FontAwesomeIcon icon={faTruck} className="text-500 mr-2" />
        {formatVisitFee(service)}
      </p>
      {service.available_days?.length > 0 && (
        <p className="fs--1 text-700 mb-3">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-500 mr-2" />
          {formatAvailability(service.available_days, service.available_from, service.available_to)}
        </p>
      )}
      {service.open_request ? (
        <OpenRequestAlert request={service.open_request} />
      ) : (
        <Button color="primary" block onClick={onRequest}>
          Solicitar visita / Cotizar
        </Button>
      )}
    </CardBody>
  </Card>
)

const MarketplaceServiceDetail = () => {
  const { id } = useParams()
  const serviceRequest = useSWRFetch(['marketplace-service', id], (key, serviceId) => getMarketplaceService(serviceId))
  const [modalKey, setModalKey] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)

  const service = serviceRequest.data?.data

  const openModal = () => {
    setModalKey((key) => key + 1)
    setModalOpen(true)
  }

  const handleSent = (request) => {
    setModalOpen(false)
    serviceRequest.mutate((current) => ({ ...current, data: { ...current.data, open_request: request } }), false)
  }

  if (serviceRequest.error) {
    return (
      <Alert color="warning">
        Este servicio ya no está disponible en el Marketplace.
        <Link to="/dashboard/marketplace" className="ml-1">
          Ver otros servicios
        </Link>
      </Alert>
    )
  }

  if (serviceRequest.isLoading || !service) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  return (
    <>
      <Button tag={Link} to="/dashboard/marketplace" color="link" size="sm" className="px-0 mb-2">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        Volver al Marketplace
      </Button>

      <Row>
        <Col lg={8}>
          <Card className="mb-3 overflow-hidden">
            <Gallery photos={service.photos || []} />
          </Card>

          <ServiceSummaryCard service={service} onRequest={openModal} className="d-lg-none" />

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h6 className="mb-0">Qué incluye</h6>
            </CardHeader>
            <CardBody>
              <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                {service.description}
              </p>
            </CardBody>
          </Card>

          <ReviewsSection />
        </Col>

        <Col lg={4}>
          <ServiceSummaryCard service={service} onRequest={openModal} className="d-none d-lg-block" />

          {service.provider && <ProviderCard provider={service.provider} />}
        </Col>
      </Row>

      <ServiceRequestModal
        key={modalKey}
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        service={service}
        onSent={handleSent}
      />
    </>
  )
}

export default MarketplaceServiceDetail
